<template>
  <div class="after-sale-detail">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <el-button type="text" icon="el-icon-arrow-left" @click="$router.back()">返回</el-button>
        <span class="title">售后详情</span>
      </div>

      <div v-if="detail.id" v-loading="loading">
        <!-- 基本信息 -->
        <el-divider content-position="left">基本信息</el-divider>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="售后单号">{{ detail.after_sale_no }}</el-descriptions-item>
          <el-descriptions-item label="订单号">{{ detail.order_no }}</el-descriptions-item>
          <el-descriptions-item label="售后状态">
            <el-tag :type="statusTagType(detail.status)" size="small">{{ detail.status_text }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="售后类型">{{ detail.after_sale_type_text }}</el-descriptions-item>
          <el-descriptions-item label="退款金额"><span class="amount-text">&yen;{{ detail.refund_amount }}</span></el-descriptions-item>
          <el-descriptions-item label="商品类型">{{ detail.goods_type_text }}</el-descriptions-item>
          <el-descriptions-item label="申请人">{{ detail.member_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.member_phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="申请时间">{{ detail.create_time }}</el-descriptions-item>
        </el-descriptions>

        <!-- 售后原因 -->
        <el-divider content-position="left">售后原因</el-divider>
        <p style="margin:0 0 8px"><strong>{{ detail.reason }}</strong></p>
        <p v-if="detail.description" style="margin:0 0 12px;color:#606266">{{ detail.description }}</p>
        <div v-if="detail.evidence_images && detail.evidence_images !== '[]'" class="evidence-images">
          <el-image
            v-for="(img, idx) in parseImages(detail.evidence_images)"
            :key="idx"
            :src="img"
            style="width:80px;height:80px;margin-right:8px;border-radius:4px"
            fit="cover"
            :preview-src-list="parseImages(detail.evidence_images)"
          />
        </div>

        <!-- 订单商品 -->
        <el-divider content-position="left">订单商品</el-divider>
        <el-table :data="detail.order_items" border size="small">
          <el-table-column label="商品" min-width="200">
            <template slot-scope="scope">
              <div class="goods-info">
                <el-image :src="scope.row.goods_img" style="width:48px;height:48px;border-radius:4px" fit="cover" />
                <span>{{ scope.row.goods_name }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="sku_spec_text" label="规格" width="120" />
          <el-table-column prop="sell_price" label="单价" width="100" />
          <el-table-column prop="buy_num" label="数量" width="80" />
          <el-table-column prop="sub_total" label="小计" width="100">
            <template slot-scope="scope">&yen;{{ scope.row.sub_total }}</template>
          </el-table-column>
        </el-table>

        <!-- 订单信息 -->
        <el-divider content-position="left">关联订单信息</el-divider>
        <el-descriptions :column="3" border size="small" v-if="detail.order">
          <el-descriptions-item label="订单金额">&yen;{{ detail.order.total_amount }}</el-descriptions-item>
          <el-descriptions-item label="支付方式">{{ detail.order.pay_type }}</el-descriptions-item>
          <el-descriptions-item label="支付时间">{{ detail.order.pay_time || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ orderStatusText(detail.order.order_status) }}</el-descriptions-item>
          <el-descriptions-item label="物流单号">{{ detail.order.logistics_no || '-' }}</el-descriptions-item>
          <el-descriptions-item label="收货地址">{{ detail.order.address_info || '-' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 费用明细 -->
        <el-divider content-position="left">费用明细</el-divider>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="邀请人">{{ detail.inviter_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="邀请人佣金">&yen;{{ detail.inviter_reward || '0.00' }}</el-descriptions-item>
          <el-descriptions-item label="平台收入">&yen;{{ detail.platform_income || '0.00' }}</el-descriptions-item>
          <el-descriptions-item label="预留奖励">&yen;{{ detail.reserve_reward || '0.00' }}</el-descriptions-item>
          <el-descriptions-item label="分身推荐奖励">&yen;{{ detail.branch_inviter_reward || '0.00' }}</el-descriptions-item>
          <el-descriptions-item label="是否已递进">{{ detail.is_progressed ? '是' : '否' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 退货物流 -->
        <template v-if="detail.after_sale_type === 1 && detail.return_express_no">
          <el-divider content-position="left">退货物流</el-divider>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="快递公司">{{ detail.return_express_company }}</el-descriptions-item>
            <el-descriptions-item label="快递单号">{{ detail.return_express_no }}</el-descriptions-item>
            <el-descriptions-item label="退货备注" :span="2">{{ detail.return_express_remark || '-' }}</el-descriptions-item>
            <el-descriptions-item label="退货时间">{{ detail.return_shipping_time || '-' }}</el-descriptions-item>
          </el-descriptions>
        </template>

        <!-- 审核信息 -->
        <el-divider content-position="left">审核信息</el-divider>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="审核时间">{{ detail.audit_time || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审核备注">{{ detail.audit_remark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="签收时间">{{ detail.sign_time || '-' }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ detail.refund_time || '-' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 操作日志 -->
        <el-divider content-position="left">操作日志</el-divider>
        <el-timeline>
          <el-timeline-item
            v-for="log in detail.logs"
            :key="log.id"
            :timestamp="log.create_time"
            placement="top"
          >
            <p style="margin:0;font-weight:bold">{{ log.action_text }}</p>
            <p v-if="log.remark" style="margin:4px 0 0;color:#909399;font-size:12px">{{ log.remark }}</p>
          </el-timeline-item>
        </el-timeline>

        <!-- 底部操作 -->
        <div style="text-align:center;margin-top:24px">
          <el-button v-if="detail.status === 0" type="danger" @click="doAudit('pass')">审核通过</el-button>
          <el-button v-if="detail.status === 0" type="info" @click="doAudit('reject')">审核拒绝</el-button>
          <el-button v-if="detail.status === 2" type="warning" @click="doSign">确认签收</el-button>
          <el-button @click="$router.back()">返回</el-button>
        </div>
      </div>
    </el-card>

    <!-- 审核弹窗 -->
    <el-dialog title="审核备注" :visible.sync="auditDialogVisible" width="400px">
      <el-input v-model="auditRemark" type="textarea" :rows="3" placeholder="选填" />
      <div slot="footer">
        <el-button @click="auditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAudit">确认</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAfterSaleDetail, auditAfterSale, signAfterSale } from '@/api'

export default {
  name: 'AfterSaleDetail',
  data() {
    return {
      detail: {},
      loading: false,
      auditDialogVisible: false,
      auditAction: 'pass',
      auditRemark: ''
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const id = this.$route.params.id
        this.detail = await getAfterSaleDetail(id)
      } catch (e) {
        this.$message.error('获取详情失败')
      }
      this.loading = false
    },
    statusTagType(status) {
      const map = { 0: 'warning', 1: 'info', 2: '', 3: 'info', 4: 'success', 5: 'danger', 6: 'info' }
      return map[status] || 'info'
    },
    orderStatusText(status) {
      const map = { 0: '待付款', 1: '待发货', 2: '待收货', 3: '已完成', 4: '已退款', 5: '售后申请中' }
      return map[status] || '未知'
    },
    parseImages(jsonStr) {
      try { return JSON.parse(jsonStr) } catch (e) { return [] }
    },
    doAudit(action) {
      this.auditAction = action
      this.auditRemark = ''
      this.auditDialogVisible = true
    },
    async submitAudit() {
      try {
        await auditAfterSale({
          after_sale_id: this.detail.id,
          audit_result: this.auditAction,
          audit_remark: this.auditRemark
        })
        this.$message.success(this.auditAction === 'pass' ? '审核通过' : '已拒绝')
        this.auditDialogVisible = false
        this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '操作失败')
      }
    },
    async doSign() {
      try {
        await this.$confirm('确认已收到退货商品，将自动执行退款？', '确认签收', { type: 'warning' })
        await signAfterSale({ after_sale_id: this.detail.id })
        this.$message.success('签收成功，退款处理中')
        this.loadDetail()
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '操作失败')
      }
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; align-items: center; }
.card-header .title { font-size: 16px; font-weight: bold; margin-left: 12px; }
.amount-text { color: #3a8ee6; font-weight: bold; }
.goods-info { display: flex; align-items: center; gap: 10px; }
.evidence-images { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
</style>
